import { useState, useEffect } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import getLeftNavigationState from './GetLeftNavigationState';

import useOmeMeetData from '../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetOrgUnitEntryInfoData from '../../state/omeMeetOrgUnitEntryInfo/UseOmeMeetOrgUnitEntryInfoData';
import useOmeMeetOrgUnitContactData from '../../state/omeMeetOrgUnitContact/UseOmeMeetOrgUnitContactData';
import useOmeMeetOrgUnitEntryRosterPermissionsData from '../../state/omeMeetOrgUnitEntryRosterPermissions/UseOmeMeetOrgUnitEntryRosterPermissionsData';
import useOmeMeetOrgUnitAthleteEntryData from '../../state/omeMeetOrgUnitAthleteEntry/UseOmeMeetOrgUnitAthleteEntryData';
import useOmeMeetOrgUnitAthleteEntryRelayRosterEntryData from '../../state/omeMeetOrgUnitAthleteEntryRelayRosterEntry/UseOmeMeetOrgUnitAthleteEntryRelayRosterEntryData';
import useOmeMeetOrgUnitNonAthleteEntryData from '../../state/omeMeetOrgUnitNonAthleteEntry/UseOmeMeetOrgUnitNonAthleteEntryData';
import useOmeMeetOrgUnitEntryPurchaseData from '../../state/omeMeetOrgUnitEntryPurchase/UseOmeMeetOrgUnitEntryPurchaseData';

import useMeetData from '@usas/ui-web/state/meet/UseMeetData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useNavRoutes from '@usas/ui-web/state/security/UseNavRoutes';

import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_STATE = { items: [], meetName: '', meetHost: '', meetClassification: '', meetDates: '' };

const useLeftNavigation = (viewName) => {
  const navigate = useNavigate();
  const { isReadyToRoute } = useNavRoutes();
  const [leftNavState, setLeftNavState] = useState(INITIAL_STATE);
  const { omeMeetState } = useOmeMeetData();
  const { omeMeetEntryState } = useOmeMeetEntryData();
  const { omeMeetOrgUnitEntryInfoState } = useOmeMeetOrgUnitEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryState } = useOmeMeetOrgUnitAthleteEntryData();
  const { omeMeetOrgUnitAthleteEntryRelayRosterEntryState } = useOmeMeetOrgUnitAthleteEntryRelayRosterEntryData();
  const { omeMeetOrgUnitNonAthleteEntryState } = useOmeMeetOrgUnitNonAthleteEntryData();
  const { omeMeetOrgUnitContactState } = useOmeMeetOrgUnitContactData();
  const { omeMeetOrgUnitEntryRosterPermissionsState } = useOmeMeetOrgUnitEntryRosterPermissionsData();
  const { omeMeetOrgUnitEntryPurchaseState } = useOmeMeetOrgUnitEntryPurchaseData();
  const { meetState } = useMeetData();
  const { environmentVariableState } = useEnvironmentVariableData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();

  const getMeetName = () => {
    if (omeMeetOrgUnitEntryInfoState?.objData?.meetName) {
      return `${omeMeetOrgUnitEntryInfoState?.objData?.meetName}`
    } else {
      return null;
    }
  };

  const getMeetHostName = () => {
    if (omeMeetOrgUnitEntryInfoState?.objData?.hostOrgUnitName) {
      return `${omeMeetOrgUnitEntryInfoState?.objData?.hostOrgUnitName}`
    } else {
      return null;
    }
  };

  const getMeetClassification = () => {
    if (omeMeetOrgUnitEntryInfoState?.objData?.meetClassificationName) {
      return `${omeMeetOrgUnitEntryInfoState?.objData?.meetClassificationName}`
    } else {
      return null;
    }
  };

  const getMeetDates = () => {
    if (omeMeetOrgUnitEntryInfoState?.objData?.meetStartDate || omeMeetOrgUnitEntryInfoState?.objData?.meetEndDate) {
      return `${formatDate(omeMeetOrgUnitEntryInfoState?.objData?.meetStartDate)} - ${formatDate(omeMeetOrgUnitEntryInfoState?.objData?.meetEndDate)}`
    } else {
      return null;
    }
  };

  const getTeamEntryStatus = () => {
    if (omeMeetOrgUnitEntryInfoState?.objData?.teamEntryStatus) {
      return `${omeMeetOrgUnitEntryInfoState?.objData?.teamEntryStatus}`;
    }
    else {
      return null;
    }
  }

  const onNavigateToPath = (e, path) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setLeftNavModalState({
      ...leftNavModalState,
      modalTitle: 'The information on this page has not been saved',
      displayPopUp: false,
      formStateSaved: true,
      path: ''
    });

    navigate(path);
  };

  useEffect(() => {
    if (isReadyToRoute === true && environmentVariableState.isLoaded === true) {
      const items = getLeftNavigationState(viewName, omeMeetState.route, omeMeetOrgUnitEntryInfoState.objData, omeMeetOrgUnitEntryRosterPermissionsState.objData);
      const meetName = getMeetName();
      const meetHost = getMeetHostName();
      const meetClassification = getMeetClassification();
      const meetDates = getMeetDates();
      const teamEntryStatus = getTeamEntryStatus();
      setLeftNavState({
        ...leftNavState,
        items,
        meetName,
        meetHost,
        meetClassification,
        meetDates,
        teamEntryStatus
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentVariableState, omeMeetState, meetState, omeMeetEntryState, omeMeetOrgUnitEntryInfoState, omeMeetOrgUnitAthleteEntryState,
    omeMeetOrgUnitAthleteEntryRelayRosterEntryState, omeMeetOrgUnitNonAthleteEntryState, omeMeetOrgUnitContactState,
    omeMeetOrgUnitEntryRosterPermissionsState, omeMeetOrgUnitEntryPurchaseState, viewName, isReadyToRoute]);

  return { ...leftNavState, leftNavState, onNavigateToPath, omeMeetOrgUnitEntryInfoState };
};

export default useLeftNavigation;
